import React, { useEffect, useMemo } from "react";
import { Modal, ModalBody, ModalContent, ModalHeader, ModalOverlay } from "@chakra-ui/react";
import { genBlock } from "@/utils/bem";
import { themeStore } from "@/stores/theme";
import { PostWorkDataType } from "../../hooks/usePostWork";
import { postPostWork, PostWorkRequestData } from "../../request";
import { sensitiveWordCheck } from "@/utils/string";
import { useRequest } from "../../hooks/useRequest";
import { Editor_Words } from "@/utils/lang/editor";
import { useTranslate } from "@/utils/lang";
import { PublishForm } from "@/components/bussiness/PublishForm";
import { PostWorkImageItem, PostWorkValues } from "@/components/bussiness/PublishForm/type";
import { useOwnToast } from "@/utils/misc";

const block = genBlock("c-post-modal");
const defaultPostValues: PostWorkValues = {
  title: "",
  description: "",
  publicPrompt: true,
  images: [],
};
const PostModal: React.FC<{
  open?: boolean;
  onClose: () => void;
  data: PostWorkDataType;
}> = ({ open = false, onClose, data }) => {
  const { containerBg, textColor } = themeStore.styles;
  const { imageNodes } = data;
  const toast = useOwnToast();
  const { translate } = useTranslate();

  const defaultValues = useMemo(() => {
    const images: PostWorkImageItem[] =
      imageNodes?.map((node) => ({
        id: node.alias,
        src: node.renderParams.src,
        meta: node.params.meta,
      })) ?? [];
    return {
      ...defaultPostValues,
      images,
    };
  }, [imageNodes]);

  const { execute: request, loading, result, error, clearRequestResult } = useRequest(postRequest);
  function postRequest(serverData: PostWorkRequestData, postWorkValues: PostWorkValues) {
    return sensitiveWordCheck(`${postWorkValues.title} \n ${postWorkValues.description}`).then(
      (passed) => {
        if (passed) {
          return postPostWork(serverData);
        } else {
          Promise.reject(false);
        }
      },
    );
  }

  function handleConfirm(serverData: PostWorkRequestData, postWorkValues: PostWorkValues) {
    request(serverData, postWorkValues);
  }

  function handleClose() {
    onClose?.();
  }

  useEffect(() => {
    if (error !== null) {
      if (error === false) {
        toast({
          status: "error",
          title: translate(Editor_Words["message_post_sensitive-check-failed"]),
        });
      } else {
        toast({
          status: "error",
          title: translate(Editor_Words["message_post_failed"]),
        });
      }
      clearRequestResult();
    }
  }, [error]);

  useEffect(() => {
    if (result?.code === 0) {
      toast({
        status: "info",
        title: translate(Editor_Words["message_post_success"]),
        isClosable: true,
        position: "top",
      });
      handleClose();
      clearRequestResult();
    }
  }, [result]);

  return (
    <Modal
      size={"5xl"}
      isCentered
      isOpen={open}
      onClose={onClose}
      scrollBehavior="inside"
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent
        style={{ width: "900px" }}
        rounded="16px"
        h="648px"
        padding="24px 0px 8px 0px"
        bg={containerBg}
        color={textColor}
        className={block()}
      >
        <ModalHeader fontSize="24px" padding="0px 54px 0px 54px" mb="20px">
          {translate(Editor_Words["header_post-art-work"])}
        </ModalHeader>

        <ModalBody padding="1px 54px 24px 54px">
          <PublishForm
            className={block("content")}
            onConfirm={handleConfirm}
            loading={loading}
            onCancel={handleClose}
            defaultValues={defaultValues}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
export default PostModal;
